import { useWalletInfo, useWeb3Modal, useWeb3ModalAccount, useWeb3ModalEvents, useWeb3ModalProvider, useWeb3ModalState } from "@web3modal/ethers/react";
import { message as Message } from "antd";
import Decimal from "decimal.js";
import { BrowserProvider, Contract, Interface, isAddress, parseEther, parseUnits, toQuantity } from "ethers";
import { FC, useEffect, useState } from "react";

import ERC20 from "@/abi/ERC20.json";
import Drag from "@/components/Drag";
import { StoreOrder } from "@/store/payment";
import { checkIsMobile } from "@/utils/utils";

import { getNonce, Tron, TronLink, TronWeb, useTronContractApprove } from "./useContract";

interface WalletConnectProps {
  order: StoreOrder,
  disable: boolean,
  pendingOpen?: () => void,
  pendingClose?: () => void,
  paymentSuccess?: () => void,
}

const WalletConnect: FC<WalletConnectProps> = ({ order, pendingOpen, paymentSuccess, pendingClose, disable }) => {
  const isMobile = checkIsMobile();
  const [hasSelected, setHasSelected] = useState<string>("");
  const { contract_pay_address, pay_address, amount, trade_no, contract, decimal, token, chain_id, chain } = order;
  const [loading, setLoading] = useState<boolean>(false);
  const { open, close } = useWeb3Modal();
  const { walletProvider } = useWeb3ModalProvider();
  const { chainId } = useWeb3ModalAccount();
  const { walletInfo } = useWalletInfo();
  const { data } = useWeb3ModalEvents();
  const { open: isOpen } = useWeb3ModalState();

  const openWallet = (href: string) => {
    let a = document.createElement("a")
    a.target = "_blank"
    a.href = href
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  const message = {
    code: -1995,
    message: "Insufficient balance"
  }

  const errorMessage = {
    code: -2000,
    message: "network error"
  }

  const TronBalanceVerify = async (address: string, session?: any): Promise<{
    address: string
    session?: any
  }> => {
    window.tronWeb?.setAddress(address)
    return new Promise(async (resolve, reject) => {
      if (contract === "TRX") {
        window.tronWeb?.trx.getBalance(address).then((balance: number) => {
          console.log(address, balance, balance / 1e6)
          if (balance / 1e6 >= +amount) {
            resolve({
              address,
              session
            })
          }
          else {
            reject(message)
          }
        }).catch(() => {
          reject(errorMessage)
        })
      }
      else {
        console.log("start fetch tron token balance-------->")
        const tokenContract = await window.tronWeb?.contract().at(contract)
        const data = await tokenContract.balanceOf(address).call().catch(() => reject(errorMessage))
        const balance = parseInt(data._hex, 16) / 10 ** decimal

        balance >= +amount ? resolve({ address, session }) : reject(message)
      }
    })
  }

  const TronLinkSend = async (tronWeb: TronWeb, to: string, amount: string, token?: string) => {
    if (token) {
      let contract = await tronWeb.contract().at(token)
      return contract.transfer(to, amount).send({
        feeLimit: 100_000_000,
        callValue: 0,
        // shouldPollResponse: true
      })
    }

    let tx = await tronWeb.transactionBuilder.sendTrx(to, +amount)
    let signedTx = await tronWeb.trx.sign(tx)
    return tronWeb.trx.sendRawTransaction(signedTx)
  }

  const tronLinkPayment = async () => {
    const tronLink = TronWeb()

    if (tronLink) {
      return new Promise(async (resolve, reject) => {
        new Promise<Exclude<ReturnType<typeof TronLink>, null>>(_resolve => {
          let tronWeb: ReturnType<typeof TronLink> = null,
            timer: ReturnType<typeof setInterval> | null = null
          timer = setInterval(() => {
            tronWeb = TronWeb()
            if (tronWeb && timer) {
              _resolve(tronWeb)
              clearInterval(timer)
            }
          }, 100)
        }).then(async tronWeb => {
          if (contract_pay_address) {
            let tronContract = await tronWeb.contract().at(contract_pay_address)
            if (contract === "TRX") {
              tronContract["transfer(string)"](
                trade_no
              ).send({
                feeLimit: 100_000_000,
                callValue: Decimal.mul(amount, Math.pow(10, decimal)).toNumber()
              }).then(resolve).catch(reject)
            }
            else {
              const tokenContract = await tronWeb.contract().at(contract)
              useTronContractApprove(
                tokenContract,
                tronWeb.defaultAddress.base58,
                contract_pay_address,
                +amount
              ).then(() => {
                tronContract["transfer(string,address,uint256)"](
                  trade_no,
                  contract,
                  parseUnits(amount, decimal)
                ).send({
                  feeLimit: 100_000_000,
                  callValue: 0
                }).then(resolve).catch(reject)
              }).catch(reject)
            }
          }
          else {
            TronLinkSend(
              tronWeb,
              pay_address,
              Decimal.mul(amount, Math.pow(10, decimal)).toString(),
              contract === "TRX" ? void 0 : contract
            ).then(resolve).catch(reject)
          }
        })
      })
    }
  }

  const connectTronLink = async () => {
    try {
      const tron = Tron()
      const tronLink = TronLink()

      if (tronLink) {
        return new Promise(async (resolve, reject) => {
          if (!tronLink.ready) {
            let requestAccountsResponse = await tronLink.request<Record<string, any>>(
              {
                method: "tron_requestAccounts",
                params: {
                  websiteIcon: "<WEBSITE ICON URI>",
                  websiteName: "<WEBSITE NAME>",
                }
              }
            )

            if (requestAccountsResponse.code !== 200) {
              console.log("get tron accounts failed: ", requestAccountsResponse)
              reject(requestAccountsResponse)
              return
            }
          }
          if (tron) {
            try {
              await (isMobile ? tronLink : tron).request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: `0x${chain_id.toString(16)}` }],
              })
            } catch (err) {
              console.log("tron wallet_switchEthereumChain failed: ", err, "tr")
              close();
              loading && setLoading(false)
              reject(err)
              return
            }
          }

          const res: any = await new Promise<Exclude<ReturnType<typeof TronLink>, null>>(_resolve => {
            let tronWeb: ReturnType<typeof TronLink> = null,
              timer: ReturnType<typeof setInterval> | null = null
            timer = setInterval(() => {
              tronWeb = TronWeb()
              if (tronWeb && timer) {
                _resolve(tronWeb)
                clearInterval(timer)
              }
            }, 100)
          }).catch((err) => {
            reject(err)
          })

          if (!res?.defaultAddress?.base58) {
            reject("No tron web!")
          }

          console.log("tron wallet default address: ", res.defaultAddress.base58)
          const data = await TronBalanceVerify(res.defaultAddress.base58).catch(reject)
          console.log("tron wallet address: ", data)
          resolve(data);
          close()
          loading && setLoading(false);
        })
      }
      else {
        let tronURLParams = encodeURIComponent(JSON.stringify({
          "url": window.location.href,
          "action": "open",
          "protocol": "tronlink",
          "version": "1.0"
        }))
        openWallet(`tronlinkoutside://pull.activity?param=${tronURLParams}`)
        return Promise.reject({})
      }
    } catch (err) {
    }
  }

  const handleTronLink = async () => {
    try {
      setLoading(true);
      pendingOpen?.();
      const { address } = await connectTronLink() as any;
      console.log("tronLink wallet address------>", address);
      const res = await tronLinkPayment();
      console.log("tron transfer success: ", res)
      paymentSuccess?.()
    } catch (error) {
      console.error("connect tronLink failed", error);
      close()
      setLoading(false);
      Message.open({
        content: error.message,
        icon: <img src={require("@/assets/warning.png")} />
      })
    }
  }

  const handleWalletConnect = async () => {
    try {
      await open({ view: "Connect" });
    } catch (error) {
      console.log("wallet connect error: ", error)
    }
  }

  const approveToken = async (provider: BrowserProvider) => {
    try {
      const { contract, contract_pay_address, amount, decimal, token, chain } = order
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      console.log('approve chain ----->', chain, address, contract);
      const tokenContract = new Contract(
        contract,
        ERC20,
        signer
      );
      const formatAmount = parseUnits(amount, decimal);
      const allowance = await tokenContract.allowance(address, contract_pay_address).catch((error) => {
        if (error.message.indexOf('value="0x"') > 0) {
          console.log("allowance is none")
          return BigInt(0)
        }
      });

      console.log(formatAmount, allowance, formatAmount > allowance, address)

      if (formatAmount > allowance) {
        console.log("start approve------>", parseUnits(amount, decimal));
        const apvTx = await tokenContract.approve(contract_pay_address, parseUnits(amount, decimal));
        await apvTx.wait();
        console.log(`${token} approved!`, apvTx.hash);
        return;
      }

      console.log("allowance is enough")
    } catch (error) {
      throw error;
    }
  }

  const startTransaction = async (provider: BrowserProvider) => {
    try {
      const { pay_address, amount, decimal, contract_pay_address, contract, trade_no, chain_info } = order
      let data: string | undefined;
      let value: string | undefined;
      let toAddr = pay_address;
      const formatAmount = parseUnits(amount, decimal);
      const singer = await provider.getSigner();
      const address = await singer.getAddress();
      console.log("current address: ", address);
      const network = await provider.getNetwork();
      console.log("current network: ", network);
      const nonce = await getNonce(chain_info.rpc, address)
      console.log("\ncurrent nonce: ", nonce);
      const ABI = JSON.parse(chain_info.abi)

      if (contract_pay_address && isAddress(contract)) {
        toAddr = contract_pay_address
        data = new Interface(ABI).encodeFunctionData(
          "transfer(string,address,uint256)",
          [
            trade_no,
            contract,
            formatAmount
          ]
        )
        console.log("transfer token params: ", toAddr, "transfer(string,address,unit256)", [trade_no, contract, formatAmount])
      }

      if (contract_pay_address && !isAddress(contract)) {
        data = new Interface(ABI).encodeFunctionData(
          "transfer(string)",
          [trade_no]
        );
        value = toQuantity(formatAmount);
        toAddr = contract_pay_address;
        console.log("transfer token params: ", toAddr, "transfer(string)", [trade_no], "value: ", value)
      }

      if (!contract_pay_address && isAddress(contract)) {
        data = new Interface(ERC20).encodeFunctionData(
          "transfer",
          [pay_address, formatAmount]
        );
        console.log("transfer token params data: ", contract, "transfer", [pay_address, formatAmount])
      }

      if (!contract_pay_address && !isAddress(contract)) {
        value = toQuantity(parseEther(amount));
        console.log("transfer coin params: ", toAddr, value)
      }

      const txData: Record<string, any> = {
        from: address,
        to: toAddr,
      };

      if (data) {
        txData.data = data
      }

      if (value) {
        txData.value = value
      }

      console.log(txData)
      const tx = await singer.sendTransaction({ ...txData, blockTag: "latest", nonce })
      const transaction = await provider.waitForTransaction(tx.hash, 1);
      console.log("transaction wait for one confirmed success: ", transaction);

      if (!transaction) {
        throw new Error("Transaction not found!")
      }

      await tx.wait()
      console.log(tx.hash)
      return tx.hash
    } catch (error) {
      if (error.code === "UNKNOWN_ERROR") {
        throw new Error("Network error")
      }

      throw error;
    }
  }

  const onSignMessage = async () => {
    try {
      setLoading(true);
      pendingOpen?.();
      if (!walletProvider) throw Error('Wallet provider not found!');

      // 检查是否已获得用户授权
      const accounts = await walletProvider.request({ method: "eth_accounts" });

      if (!accounts || accounts.length === 0) {
        await walletProvider.request({ method: "eth_requestAccounts" });
      }

      if (chainId !== chain_id) {
        console.log("start switch chain");
        try {
          await walletProvider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: `0x${Number(chain_id).toString(16)}` }] // 目标链 ID 转换为十六进制
          });
          console.log("switch chain success!");
        } catch (err) {
          console.error('start switch chain failed: ', err);
          throw new Error("Switch chain failed!")
        }
      }

      const provider = new BrowserProvider(walletProvider);

      if (isAddress(contract)) {
        await approveToken(provider);
      }

      await startTransaction(provider);
      setHasSelected("");
      setLoading(false);
      paymentSuccess?.();
    } catch (error) {
      console.error("transaction error: ", error);
      Message.open({
        content: error.message,
        icon: <img src={require("@/assets/warning.png")} />
      })
      setHasSelected("");
      setLoading(false);
      pendingClose?.();
    }
  }

  const handleEventChange = async (data: any) => {
    if (data.event === 'CONNECT_SUCCESS') {
      setHasSelected(data.properties.name);
    } else if (data.event === "SELECT_WALLET") {
      console.log("selected wallet------>", data.properties);
      data.properties.name === "TronLink" && await handleTronLink();
      data.properties.name === "WalletConnect" && setHasSelected("WalletConnect");
    } else if (data.event === "MODAL_CLOSE") {
      // wallet qrcode connected
      console.log("MODAL CLOSE: ", data, walletInfo);
    } else {
      loading && setLoading(false);
    }
  }

  useEffect(() => {
    if (!data.event) {
      return;
    }

    handleEventChange(data)
  }, [data.event]);

  useEffect(() => {
    if (isOpen) {
      return
    }

    // if (hasSelected === "WalletConnect" && walletProvider && !loading) {
    //   console.log(walletProvider)
    //   onSignMessage();
    //   return
    // }

    if (hasSelected && walletProvider && !loading) {
      console.log("start transaction");
      onSignMessage();
      return
    }
  }, [walletProvider, hasSelected, loading, isOpen])

  return (
    <Drag
      loading={loading}
      disabled={loading || disable}
      key="walletconnect"
    >
      <div
        className="cashier-wallet"
        onClick={handleWalletConnect}
      >
        <img
          className="cashier-wallet-icon"
          src={require("@/assets/wallet_connect.png")}
        />
        <span>WalletConnect</span>
      </div>
    </Drag>
  )
}

export default WalletConnect
